import React, { createContext, useContext, useState, useCallback, Suspense, lazy } from 'react';
import type { CallBackProps, Step, Locale } from 'react-joyride';
import { useTranslation } from 'react-i18next';

// Lazy-load react-joyride (~20KB gzipped) — only downloaded when a tour actually starts
const Joyride = lazy(() => import('react-joyride'));

interface TourContextType {
  startTour: (tourId: string) => void;
  stopTour: () => void;
  resetTour: (tourId: string) => void;
  autoStartTour: (tourId: string) => void;
  isTourActive: boolean;
  currentTourId: string | null;
}

const TourContext = createContext<TourContextType | undefined>(undefined);

interface TourProviderProps {
  children: React.ReactNode;
}

// Custom styles pour correspondre au design de l'app
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const tourStyles: any = {
  options: {
    primaryColor: '#3b82f6', // blue-500
    textColor: '#1f2937', // gray-800
    backgroundColor: '#ffffff',
    overlayColor: 'rgba(0, 0, 0, 0.5)',
    arrowColor: '#ffffff',
    zIndex: 10000,
  },
  tooltip: {
    borderRadius: '0.5rem',
    padding: '1rem',
    fontSize: '0.8125rem', // 13px
  },
  tooltipContent: {
    padding: '0.5rem 0',
    fontSize: '0.8125rem', // 13px
  },
  tooltipTitle: {
    fontSize: '0.875rem', // 14px
    fontWeight: '600',
  },
  buttonNext: {
    backgroundColor: '#3b82f6',
    borderRadius: '0.375rem',
    padding: '0.5rem 1rem',
    fontSize: '0.8125rem', // 13px
  },
  buttonBack: {
    color: '#6b7280',
    marginRight: '0.5rem',
    fontSize: '0.8125rem', // 13px
  },
  buttonClose: {
    padding: '0.5rem',
    fontSize: '0.8125rem', // 13px
  },
  buttonSkip: {
    fontSize: '0.8125rem', // 13px
  },
};

// Structure d'un step sans contenu (le contenu vient des traductions)
interface TourStepDef {
  target: string;
  disableBeacon?: boolean;
}

// Convertit un tour ID kebab-case en clé camelCase pour les traductions
function tourIdToKey(tourId: string): string {
  return tourId.replace(/-([a-z])/g, (_, c) => c.toUpperCase());
}

// Résout les steps d'un tour en injectant le contenu traduit
// eslint-disable-next-line @typescript-eslint/no-explicit-any
function resolveSteps(tourId: string, stepDefs: TourStepDef[], t: (key: string) => any): Step[] {
  const key = tourIdToKey(tourId);
  return stepDefs.map((def, index) => ({
    target: def.target,
    content: t(`tours.${key}.${index + 1}`),
    ...(def.disableBeacon ? { disableBeacon: true } : {}),
  }));
}

export const TourProvider: React.FC<TourProviderProps> = ({ children }) => {
  const { t } = useTranslation('tour');

  const locale: Locale = {
    back: t('buttons.back'),
    close: t('buttons.close'),
    last: t('buttons.last'),
    next: t('buttons.next'),
    open: t('buttons.open'),
    skip: t('buttons.skip'),
  };

  function addStepNumbers(steps: Step[]): Step[] {
    return steps.map((step, index) => ({
      ...step,
      title: t('stepLabel', { current: index + 1, total: steps.length }) + (step.title ? ' : ' + step.title : ''),
    }));
  }
  const [run, setRun] = useState(false);
  const [currentTourId, setCurrentTourId] = useState<string | null>(null);
  const [steps, setSteps] = useState<Step[]>([]);

  const startTour = useCallback((tourId: string) => {
    const stepDefs = tourRegistry[tourId];
    if (!stepDefs) {
      console.warn(`Tour "${tourId}" not found`);
      return;
    }

    setCurrentTourId(tourId);
    const resolved = resolveSteps(tourId, stepDefs, t);
    setSteps(addStepNumbers(resolved));

    // Délai pour s'assurer que les éléments DOM sont montés et les données chargées
    setTimeout(() => {
      setRun(true);
    }, 800);
  }, [t]);

  const stopTour = useCallback(() => {
    setRun(false);
    setCurrentTourId(null);
  }, []);

  const resetTour = useCallback((tourId: string) => {
    localStorage.removeItem(`tour_completed_${tourId}`);
  }, []);

  const autoStartTour = useCallback((tourId: string) => {
    // Vérifier si le tour a déjà été complété
    const completed = localStorage.getItem(`tour_completed_${tourId}`);
    if (!completed) {
      // Démarrer le tour automatiquement
      startTour(tourId);
    }
  }, [startTour]);

  const handleJoyrideCallback = useCallback((data: CallBackProps) => {
    const { status } = data;

    if (['finished', 'skipped'].includes(status)) {
      if (currentTourId) {
        // Marquer le tour comme complété
        localStorage.setItem(`tour_completed_${currentTourId}`, 'true');
      }
      setRun(false);
      setCurrentTourId(null);
    }
  }, [currentTourId]);

  return (
    <TourContext.Provider
      value={{
        startTour,
        stopTour,
        resetTour,
        autoStartTour,
        isTourActive: run,
        currentTourId,
      }}
    >
      {children}
      {run && (
        <Suspense fallback={null}>
          <Joyride
            steps={steps}
            run={run}
            continuous
            showProgress={false}
            showSkipButton
            scrollToFirstStep
            disableOverlayClose
            spotlightClicks
            styles={tourStyles}
            locale={locale}
            callback={handleJoyrideCallback}
          />
        </Suspense>
      )}
    </TourContext.Provider>
  );
};

export const useTour = () => {
  const context = useContext(TourContext);
  if (!context) {
    throw new Error('useTour must be used within a TourProvider');
  }
  return context;
};

// Registry des tours — structure uniquement (le contenu textuel vient de tour.json)
const tourRegistry: Record<string, TourStepDef[]> = {
  // Stock - Liste des produits
  'stock-products-list': [
    { target: '[data-tour="products-categories"]', disableBeacon: true },
    { target: '[data-tour="products-search"]' },
    { target: '[data-tour="products-filters"]' },
    { target: '[data-tour="products-type-filter"]' },
    { target: '[data-tour="products-expand-all"]' },
    { target: '[data-tour="products-col-product"]' },
    { target: '[data-tour="products-col-category"]' },
    { target: '[data-tour="products-col-variants"]' },
    { target: '[data-tour="products-col-alert"]' },
    { target: '[data-tour="products-col-optimal"]' },
    { target: '[data-tour="products-col-stock"]' },
    { target: '[data-tour="products-col-status"]' },
    { target: '[data-tour="products-col-actions"]' },
    { target: '[data-tour="products-side-panel"]' },
    { target: '[data-tour="products-panel-stock"]' },
    { target: '[data-tour="products-panel-suppliers"]' },
    { target: '[data-tour="products-panel-lots"]' },
    { target: '[data-tour="products-panel-btn-details"]' },
    { target: '[data-tour="products-add"]' },
  ],

  // Stock - Produits archivés
  'stock-products-archived': [
    { target: '[data-tour="archived-header"]', disableBeacon: true },
    { target: '[data-tour="archived-search"]' },
    { target: '[data-tour="archived-products"]' },
    { target: '[data-tour="archived-variants"]' },
  ],

  // Stock - Formulaire produit
  'stock-product-form': [
    { target: '[data-tour="product-catalog"]', disableBeacon: true },
    { target: '[data-tour="product-basic-info"]' },
    { target: '[data-tour="product-image"]' },
    { target: '[data-tour="product-stock"]' },
    { target: '[data-tour="product-pricing"]' },
    { target: '[data-tour="product-variants"]' },
  ],

  // Stock - Détail produit
  'stock-product-detail': [
    { target: '[data-tour="product-detail-back"]', disableBeacon: true },
    { target: '[data-tour="product-detail-header"]' },
    { target: '[data-tour="product-detail-actions"]' },
    { target: '[data-tour="product-detail-manual-entry"]' },
    { target: '[data-tour="product-detail-adjust"]' },
    { target: '[data-tour="product-detail-edit"]' },
    { target: '[data-tour="product-detail-info"]' },
    { target: '[data-tour="product-detail-evolution"]' },
    { target: '[data-tour="product-detail-stock-chart"]' },
    { target: '[data-tour="product-detail-price-chart"]' },
    { target: '[data-tour="product-detail-variants"]' },
    { target: '[data-tour="product-detail-suppliers"]' },
    { target: '[data-tour="product-detail-movements"]' },
  ],

  // Stock - Réception
  'stock-reception': [
    { target: '[data-tour="reception-header"]', disableBeacon: true },
    { target: '[data-tour="reception-bc"]' },
    { target: '[data-tour="reception-scanner"]' },
  ],

  // Stock - Fournisseurs
  'stock-suppliers': [
    { target: '[data-tour="suppliers-search"]', disableBeacon: true },
    { target: '[data-tour="suppliers-add"]' },
    { target: '[data-tour="suppliers-table"]' },
  ],

  // Stock - Formulaire fournisseur
  'stock-supplier-form': [
    { target: '[data-tour="supplier-form-header"]', disableBeacon: true },
    { target: '[data-tour="supplier-form-selection"]' },
    { target: '[data-tour="supplier-form-search"]' },
    { target: '[data-tour="supplier-form-custom"]' },
    { target: '[data-tour="supplier-form-cabinet-info"]' },
    { target: '[data-tour="supplier-form-order-mode"]' },
    { target: '[data-tour="supplier-form-shipping"]' },
  ],

  // Stock - Détail fournisseur
  'stock-supplier-detail': [
    { target: '[data-tour="supplier-detail-header"]', disableBeacon: true },
    { target: '[data-tour="supplier-kpi-orders"]' },
    { target: '[data-tour="supplier-kpi-spent"]' },
    { target: '[data-tour="supplier-kpi-last-order"]' },
    { target: '[data-tour="supplier-kpi-invoices"]' },
    { target: '[data-tour="supplier-kpi-avg-order"]' },
    { target: '[data-tour="supplier-kpi-delivery"]' },
    { target: '[data-tour="supplier-kpi-reception"]' },
    { target: '[data-tour="supplier-kpi-products"]' },
    { target: '[data-tour="supplier-detail-evolution"]' },
    { target: '[data-tour="supplier-detail-period"]' },
    { target: '[data-tour="supplier-detail-orders"]' },
    { target: '[data-tour="supplier-detail-invoices"]' },
  ],

  // Stock - Commande intelligente
  'stock-smart-order': [
    { target: '[data-tour="smart-order-header"]', disableBeacon: true },
    { target: '[data-tour="smart-order-generate"]' },
    { target: '[data-tour="smart-order-tabs"]' },
    { target: '[data-tour="smart-order-info"]' },
  ],

  // Stock - Anomalies
  'stock-anomalies': [
    { target: '[data-tour="anomalies-header"]', disableBeacon: true },
    { target: '[data-tour="anomalies-detect"]' },
    { target: '[data-tour="anomalies-stats"]' },
  ],

  // Stock - Journal
  'stock-journal': [
    { target: '[data-tour="journal-header"]', disableBeacon: true },
    { target: '[data-tour="journal-filters-btn"]' },
  ],

  // Stock - Commandes
  'stock-orders': [
    { target: '[data-tour="orders-create"]', disableBeacon: true },
    { target: '[data-tour="orders-filters"]' },
    { target: '[data-tour="orders-table"]' },
  ],

  // Stock - Mouvements
  'stock-movements': [
    { target: '[data-tour="movements-filters"]', disableBeacon: true },
    { target: '[data-tour="movements-date"]' },
    { target: '[data-tour="movements-table"]' },
  ],

  // Stock - Péremptions
  'stock-expiries': [
    { target: '[data-tour="expiries-header"]', disableBeacon: true },
    { target: '[data-tour="expiries-alerts"]' },
    { target: '[data-tour="expiries-filters"]' },
  ],

  // Stock - Inventaire
  'stock-inventory': [
    { target: '[data-tour="inventory-start"]', disableBeacon: true },
    { target: '[data-tour="inventory-scan"]' },
    { target: '[data-tour="inventory-diff"]' },
    { target: '[data-tour="inventory-validate"]' },
  ],

  // Stock - Panier
  'stock-cart': [
    { target: '[data-tour="cart-header"]', disableBeacon: true },
    { target: '[data-tour="cart-summary"]' },
    { target: '[data-tour="cart-add"]' },
    { target: '[data-tour="cart-add-panel"]' },
    { target: '[data-tour="cart-groups"]' },
    { target: '[data-tour="cart-supplier-group"]' },
    { target: '[data-tour="cart-franco"]' },
    { target: '[data-tour="cart-create-bc"]' },
    { target: '[data-tour="cart-summary-multi"]' },
    { target: '[data-tour="cart-generate-all"]' },
    { target: '[data-tour="cart-clear"]' },
  ],

  // Stock - Factures
  'stock-invoices': [
    { target: '[data-tour="invoices-upload"]', disableBeacon: true },
    { target: '[data-tour="invoices-filters"]' },
    { target: '[data-tour="invoices-list"]' },
    { target: '[data-tour="invoices-rematch"]' },
    { target: '[data-tour="invoices-associate"]' },
  ],

  // Stock - Export comptable
  'stock-export': [
    { target: '[data-tour="export-parameters"]', disableBeacon: true },
    { target: '[data-tour="export-date"]' },
    { target: '[data-tour="export-preview"]' },
    { target: '[data-tour="export-csv"]' },
    { target: '[data-tour="export-excel"]' },
    { target: '[data-tour="export-summary"]' },
  ],

  // Stock - Dashboard (inclut sidebar)
  'stock-dashboard': [
    { target: 'aside', disableBeacon: true },
    { target: '[data-tour="sidebar-dashboard"]' },
    { target: '[data-tour="sidebar-sortie"]' },
    { target: '[data-tour="sidebar-products"]' },
    { target: '[data-tour="sidebar-products-archived"]' },
    { target: '[data-tour="sidebar-suppliers"]' },
    { target: '[data-tour="sidebar-cart"]' },
    { target: '[data-tour="sidebar-orders"]' },
    { target: '[data-tour="sidebar-smart-order"]' },
    { target: '[data-tour="sidebar-reception"]' },
    { target: '[data-tour="sidebar-invoices"]' },
    { target: '[data-tour="sidebar-expirations"]' },
    { target: '[data-tour="sidebar-predictions"]' },
    { target: '[data-tour="sidebar-anomalies"]' },
    { target: '[data-tour="sidebar-journal"]' },
    { target: '[data-tour="sidebar-export"]' },
    { target: '[data-tour="sidebar-settings"]' },
    { target: '[data-tour="dashboard-kpis"]' },
    { target: '[data-tour="dashboard-kpi-value"]' },
    { target: '[data-tour="dashboard-kpi-lots"]' },
    { target: '[data-tour="dashboard-kpi-variants"]' },
    { target: '[data-tour="dashboard-kpi-ruptures"]' },
    { target: '[data-tour="dashboard-kpi-low"]' },
    { target: '[data-tour="dashboard-kpi-expirations"]' },
    { target: '[data-tour="dashboard-donut"]' },
    { target: '[data-tour="dashboard-chart"]' },
    { target: '[data-tour="dashboard-chart-period"]' },
    { target: '[data-tour="dashboard-top-value"]' },
    { target: '[data-tour="dashboard-top-consumption"]' },
    { target: '[data-tour="dashboard-top-suppliers"]' },
    { target: '[data-tour="dashboard-treemap"]' },
    { target: '[data-tour="dashboard-actions"]' },
    { target: '[data-tour="dashboard-action-reception"]' },
    { target: '[data-tour="dashboard-action-sortie"]' },
    { target: '[data-tour="dashboard-consumers"]' },
    { target: '[data-tour="dashboard-cart"]' },
  ],

  // Stock - Sortie
  'stock-sortie': [
    { target: '[data-tour="sortie-scan-bar"]', disableBeacon: true },
    { target: '[data-tour="sortie-consumer-panel"]' },
    { target: '[data-tour="sortie-motif"]' },
    { target: '[data-tour="sortie-input"]' },
    { target: '[data-tour="sortie-entries"]' },
    { target: '[data-tour="sortie-unidentified"]' },
    { target: '[data-tour="sortie-fefo-info"]' },
  ],

  // Stock - Paramètres
  'stock-settings': [
    { target: '[data-tour="settings-categories"]', disableBeacon: true },
    { target: '[data-tour="settings-preferences"]' },
    { target: '[data-tour="settings-locations"]' },
    { target: '[data-tour="settings-consumers"]' },
    { target: '[data-tour="settings-consumers-toggle"]' },
    { target: '[data-tour="settings-consumers-add"]' },
    { target: '[data-tour="settings-consumers-list"]' },
  ],

  // Stock - Détail consommateur
  'stock-consumer-detail': [
    { target: '[data-tour="consumer-detail-header"]', disableBeacon: true },
    { target: '[data-tour="consumer-detail-period"]' },
    { target: '[data-tour="consumer-detail-stats"]' },
    { target: '[data-tour="consumer-detail-evolution"]' },
    { target: '[data-tour="consumer-detail-top"]' },
    { target: '[data-tour="consumer-detail-movements"]' },
    { target: '[data-tour="consumer-detail-qr"]' },
  ],

  // Stock - Prédictions
  'stock-predictions': [
    { target: '[data-tour="predictions-horizon"]', disableBeacon: true },
    { target: '[data-tour="predictions-summary"]' },
    { target: '[data-tour="predictions-summary-rupture"]' },
    { target: '[data-tour="predictions-summary-low"]' },
    { target: '[data-tour="predictions-summary-ok"]' },
    { target: '[data-tour="predictions-summary-dormant"]' },
    { target: '[data-tour="predictions-summary-threshold"]' },
    { target: '[data-tour="predictions-rupture"]' },
    { target: '[data-tour="predictions-rupture-commander"]' },
    { target: '[data-tour="predictions-low"]' },
    { target: '[data-tour="predictions-ok"]' },
    { target: '[data-tour="predictions-dormant"]' },
    { target: '[data-tour="predictions-thresholds"]' },
  ],
};
